import type {
  RouteLocationNormalizedGeneric,
  RouteLocationRaw,
} from 'vue-router'
import { getHash, getPath, getQuery } from './path'

interface Params {
  salesChannel?: string
  locale?: string
}

/**
 * Validates if a locale string is in the allowed locales list
 * @param locales - Array of valid locale codes
 * @param locale - Locale to validate
 * @returns The locale if valid, undefined otherwise
 */
export function validateLocale(locales: string[], locale?: string) {
  if (!locale) {
    return undefined
  }
  return locales.includes(locale) ? locale : undefined
}

/**
 * Parses a URL to extract locale parameters from the path
 * @param locales - Array of valid locale codes
 * @param url - URL to parse
 * @returns Object containing extracted locale
 */
export function parseParams(
  locales: string[],
  url: RouteLocationRaw | RouteLocationNormalizedGeneric
): Params {
  const path = getPath(url)
  const parsedParams = path?.split(/\/+/).filter(Boolean) ?? [undefined]
  const locale = parsedParams[0]

  const validLocale = validateLocale(locales, locale)

  return {
    locale: validLocale,
  }
}

/**
 * Removes locale parameters from a URL path
 * @param parsedParams - Parsed parameters containing locale
 * @param url - Original URL
 * @returns URL without locale prefix
 */
export function stripTargetParams(
  parsedParams: Params,
  url: RouteLocationRaw | RouteLocationNormalizedGeneric
) {
  const path = getPath(url)
  const pathParts = path?.split(/\/+/).filter(Boolean) ?? []

  if (parsedParams.locale) {
    pathParts.shift()
  }

  return `/${pathParts.join('/')}${getQuery(url)}${getHash(url)}`
}

/**
 * Adds locale parameter to the beginning of a URL path
 * @param mandatoryLocale - Locale to add
 * @param url - URL to modify
 * @returns URL with locale prefix
 */
function addMandatoryParams(
  mandatoryLocale: string,
  url: RouteLocationRaw | RouteLocationNormalizedGeneric
) {
  const path = getPath(url)
  const pathParts = path?.split(/\/+/).filter(Boolean) ?? []

  pathParts.unshift(mandatoryLocale)

  return `/${pathParts.join('/')}${getQuery(url)}${getHash(url)}`
}

/**
 * Builds a URL for a different locale by replacing the locale prefix
 * @param locale - Target locale code
 * @param locales - Array of all valid locales
 * @param url - Original URL
 * @returns URL with target locale prefix
 *
 * @example
 * buildSwitchUrl('de', ['de', 'en', 'fr'], '/en/shop/product')
 * // Returns: '/de/shop/product'
 */
export function buildSwitchUrl(
  locale: string,
  locales: string[],
  url: RouteLocationRaw | RouteLocationNormalizedGeneric
): string {
  const parsedParams = parseParams(locales, url)

  const strippedPath = stripTargetParams(parsedParams, url)
  return addMandatoryParams(locale, strippedPath)
}
